import { GeneralApiService } from '@api/general_api.service';
import { Singleton } from '@visiba/instantiation';

/**
 * Service to load and distribute Feature Flags in the application.
 *
 * To add a new Feature Flag, add a new property to the "ExpectedFlags"
 * interface in `feature_flags.d.ts`.
 *
 * ### Related modules
 * * useFeatureFlag hook - used in React views for conditional renders.
 */
@Singleton()
export class FeatureFlagService<T extends ExpectedFlags = ExpectedFlags> {
	public static checkFlag<T extends ExpectedFlags>(flag: keyof T): boolean {
		if ((globalThis as typeof window).__visiba_feature_flags__ == null) throw new FeatureFlagStoreNotSet();

		const retrievedFlag = (globalThis as typeof window).__visiba_feature_flags__[flag as keyof ExpectedFlags];

		return retrievedFlag ?? false;
	}

	constructor(private readonly generalApiService: GeneralApiService) {
		// Empty
	}

	public async load(urlAlias: string): Promise<void> {
		(globalThis as typeof window).__visiba_feature_flags__ = undefined as never;

		const { data } = await this.generalApiService.getFeatureToggles(urlAlias);

		(globalThis as typeof window).__visiba_feature_flags__ = {
			_NOOP_EXAMPLE_DO_NOT_USE: false,
			'CareExperience.PW.Idp': false,
			'PatientWeb.UseRelativeScrollPositionInChat': false,
			'PatientWeb.UseVonage': false,
			'CareExperience.AllowedIPList': false,
			'CareExperience.VonageForceRouted': false,
			'CareExperience.VonageE2EE': false,
			'PatientWeb.ClickableLogotype': false,
			'PatientWeb.UseNewHome': false,
			'PatientWeb.UseReceptionBackground': false,
			'PatientWeb.NHS.Navigation': false,
			'PatientWeb.HideListedAtReceptions': false,
			'PatientWeb.UseRepresentatives': false,
			'PatientWeb.NHSAppPilot': false,
			'Patient.CareCompanion.HomeChatLauncher': false,
			...data,
		};
	}

	public checkFlag(toggle: keyof T): boolean {
		return FeatureFlagService.checkFlag<T>(toggle);
	}
}

export class FeatureFlagNotSet<T extends ExpectedFlags> extends Error {
	constructor(featureFlag: keyof T) {
		super(`The feature flag "${String(featureFlag)}" has not yet been registered, was it removed?`);
		this.name = 'FeatureFlagNotSet';
		Object.setPrototypeOf(this, FeatureFlagNotSet.prototype);
	}
}

export class FeatureFlagStoreNotSet extends Error {
	constructor() {
		super(`The feature flag store has not yet been declared, has the "load" method not been invoked?`);
		this.name = 'FeatureFlagStoreNotSet';
		Object.setPrototypeOf(this, FeatureFlagStoreNotSet.prototype);
	}
}
